import { Component, ViewChild, ElementRef, OnInit, OnDestroy, ViewChildren, QueryList, signal, computed, effect, NO_ERRORS_SCHEMA } from '@angular/core';
import { CdkDragDrop, CdkDragStart, CdkDragMove, moveItemInArray, CdkDragEnd } from '@angular/cdk/drag-drop';
import { ActivatedRoute, Router } from '@angular/router';
import { Utils } from '../../shared/utils';
import { FlowObjectType, FlowObjectDefinition, FlowObjectTypeDescription, FlowObjectTypeEDocsName } from '../../models/flow-object.model';
import { Grouping, GroupingType } from '../../models/grouping.model';
import { GroupingColumn } from '../../models/grouping-column.model';
import { FlowDefinition, FlowTarget, FlowTargetLabel } from '../../models/flow.model';
import { BaseComponent } from '../../components/base/base.component';
import { FlowDefinitionService } from '../../services/flow-definition.service';
import { AuthService } from '../../services/auth.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../shared/enums';
import { CookieService } from 'ngx-cookie-service';
import { TaskRules } from '../../shared/task-rules';
import { ConfigSchema, ConfigSchemaFlowDefinition } from '../../models/config-schema.model';
import { IMPORTS } from './_imports';
import { FORMBUILDER_SCHEMA_DEFAULT } from '../../components/formio/formbuilder-schema-default';
import { HTML_TEMPLATE_DEFAULT } from '../../components/details/flow-object/model-editor/tinymce-template/tinymce-template-default';
import { HTML_TEMPLATE_ACKNOWLEDGE } from '../../components/details/flow-object/model-editor/tinymce-template/tinymce-template-acknowledge';
import { HTML_TEMPLATE_APPROVE } from '../../components/details/flow-object/model-editor/tinymce-template/tinymce-template-approve';
import { HTML_TEMPLATE_EXTRA_CONTENT } from '../../components/details/flow-object/model-editor/tinymce-template/tinymce-template-extra-content';
import { OnAfterSelectFlowObjectDto } from '../../shared/helper-classes';
import { Unidade } from '../../models/organograma.model';
import { AcessoCidadaoService } from '../../services/acesso-cidadao.service';

@Component({
    selector: 'flow-definition',
    templateUrl: './flow-definition.component.html',
    styleUrl: './flow-definition.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class FlowDefinitionComponent extends BaseComponent implements OnInit, OnDestroy {
    // #region [ViewChild]
    @ViewChild('availableFlowObjectsParentRef') availableFlowObjectsParentRef: ElementRef;
    @ViewChild('availableFlowObjectsRef') availableFlowObjectsRef: ElementRef;
    @ViewChild('availableFlowObjectsWrapperRef') availableFlowObjectsWrapperRef: ElementRef;
    @ViewChild('mainPathRef') mainPathRef: ElementRef;
    @ViewChild('firstGatewayPathRef') firstGatewayPathRef: ElementRef;
    @ViewChild('secondGatewayPathRef') secondGatewayPathRef: ElementRef;
    @ViewChild('flowAreaRef') flowAreaRef: ElementRef;
    @ViewChild('flowAreaBackdropRef') flowAreaBackdropRef: ElementRef;
    @ViewChild('pageContainerRef') pageContainerRef: ElementRef;
    @ViewChildren('flowObjectBodyContainerRef') flowObjectBodyContainerRef: QueryList<ElementRef>;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    FlowObjectTypeDescription = FlowObjectTypeDescription;
    FlowTarget = FlowTarget;
    FlowTargetLabel = FlowTargetLabel;
    DetailsAreaTabs = Enums.DetailsAreaTabs;
    Enums = Enums;
    Utils = Utils;
    // #endregion

    // #region [props]
    isLatestVersion = false;
    canLoad = false;
    dragBeenOutsideAvailableArea = false;
    draggingFlowObjectIndex: any = null;
    adminUnitsList: Unidade[] = [];
    // #endregion

    // #region [signals]
    $model = signal(null as FlowDefinition);
    $selectedFlowObjects = signal([] as FlowObjectDefinition[]);
    $selectedGroupings = signal([] as Grouping[]);
    $selectedGroupingColumns = signal([] as GroupingColumn[]);
    $flowObjectToEdit = signal(null as FlowObjectDefinition);
    $flowDefinitionToEdit = signal(null as FlowDefinition);
    $availableFlowObjects = signal([] as FlowObjectDefinition[]);
    $isBootstrapFinished = signal(false);
    $selectedVersion = signal(null as number);
    $activeDetailsTab = signal(null as Enums.DetailsAreaTabs);
    $isAnyDetailsAreaOpen = signal(false);
    $isDetailsAreaExtraSize = signal(false);
    $openModalAreaTrigger = signal(false);
    $changeVersionTrigger = signal(false);
    $toggleEditAreaTrigger = signal(null as FlowObjectDefinition);
    $clearLinksTrigger = signal(false);
    $linkFlowObjectsTrigger = signal(false);
    $reorderFlowObjectsTrigger = signal(false);
    $onAfterSelectFlowObjectTrigger = signal(null as OnAfterSelectFlowObjectDto);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $hasGatewayPath = computed(() => this.$selectedFlowObjects().some(x => x.typeId == FlowObjectType.GatewayPaths));
    $hasFlowStarter = computed(() => this.$selectedFlowObjects().some(x => x.isFlowStarter));
    $hasTemporalHotConfigs = computed(() => {
        if (Utils.isNullOrEmpty(this.$model().configSchema)) return false;
        let configSchema = JSON.parse(this.$model().configSchema) as ConfigSchemaFlowDefinition;
        return configSchema.hasAutomaticFlowDefinitionDeactivation || configSchema.hasAutomaticFlowInstanceCancellation;
    });
    $isReadOnlyMode = computed(() =>
        !this.$model().isActive
        || this.$model().isPublished
        || (!!this.paramVersion && this.paramVersion < this.$model().version)
    );
    $mainPathGroupings = computed(() => this.$selectedGroupings().filter(x => !x.isFirstGatewayPath && !x.isSecondGatewayPath));
    $mainPathGroupingColumns = computed(() => this.$selectedGroupingColumns().filter(x => !x.isFirstGatewayPath && !x.isSecondGatewayPath));
    $mainPathFlowObjects = computed(() => this.$selectedFlowObjects().filter(x => !x.isFirstGatewayPath && !x.isSecondGatewayPath));
    $firstGatewayPathGroupings = computed(() => this.$selectedGroupings().filter(x => x.isFirstGatewayPath));
    $firstGatewayPathGroupingColumns = computed(() => this.$selectedGroupingColumns().filter(x => x.isFirstGatewayPath));
    $firstGatewayPathFlowObjects = computed(() => this.$selectedFlowObjects().filter(x => x.isFirstGatewayPath));
    $firstGatewayPathName = computed(() => {
        if (!this.$hasGatewayPath()) return null;
        let configSchema = JSON.parse(this.$selectedFlowObjects().find(x => x.typeId == FlowObjectType.GatewayPaths).configSchema) as ConfigSchema;
        return configSchema.taskGatewayPaths.firstGatewayPathName;
    });
    $secondGatewayPathGroupings = computed(() => this.$selectedGroupings().filter(x => x.isSecondGatewayPath));
    $secondGatewayPathGroupingColumns = computed(() => this.$selectedGroupingColumns().filter(x => x.isSecondGatewayPath));
    $secondGatewayPathFlowObjects = computed(() => this.$selectedFlowObjects().filter(x => x.isSecondGatewayPath));
    $secondGatewayPathName = computed(() => {
        if (!this.$hasGatewayPath()) return null;
        let configSchema = JSON.parse(this.$selectedFlowObjects().find(x => x.typeId == FlowObjectType.GatewayPaths).configSchema) as ConfigSchema;
        return configSchema.taskGatewayPaths.secondGatewayPathName;
    });
    // #endregion

    // #region [effects]
    $flowDefinitionToEditTrigger = effect(() => {
        let clone = Utils.clone(this.$model());
        if (clone == null) return;
        clone.flowObjectDefinitions = [...this.$selectedFlowObjects()];
        this.$flowDefinitionToEdit.set(clone);
    }, { allowSignalWrites: true });
    // #endregion

    constructor(
        route: ActivatedRoute,
        router: Router,
        spinner: NgxSpinnerService,
        toastr: ToastrService,
        private cookieService: CookieService,
        private flowDefinitionService: FlowDefinitionService,
        private acessoCidadaoService: AcessoCidadaoService,
        private authService: AuthService
    ) {
        super(route, router, spinner, toastr);

        this.canLoad = this.route.snapshot.queryParams.r == null;
        this.isLatestVersion = Utils.isNullOrEmpty(this.paramVersion);

        window.addEventListener('beforeunload', () => this.ngOnDestroy());
    }

    async ngOnInit() {
        if (!this.canLoad) return;

        try {
            const response = await this.acessoCidadaoService.getUnidadesPerfilAdministrador(this.authService.$user().id);
            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }
            this.adminUnitsList = response.data;

            if (!this.isCreate) {
                const response = await this.flowDefinitionService.getById(this.paramId, Number.isNaN(this.paramVersion) ? null : this.paramVersion, true);

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    this.router.navigate([Enums.PagesPaths.FlowDefinitionList]);
                    return;
                }

                Utils.setSignal(this.$model, response.data);

                if (Utils.isNotNullOrEmpty(this.paramVersion)) {
                    this.$selectedVersion.set(this.paramVersion);

                    if (this.paramVersion == this.$model().version) {
                        this.$changeVersionTrigger.set(true);
                    }
                } else {
                    this.$selectedVersion.set(this.$model().version);
                }
            } else {
                if (this.adminUnitsList.length == 0) {
                    this.toastr.error(Enums.Messages.NoFlowInboxError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    console.error(Enums.Messages.NoFlowInboxError);
                }

                this.$model.set(new FlowDefinition({
                    ownerId: this.$currentUser().id,
                    organizationId: this.adminUnitsList[0].organizacao.guid,
                    unitId: this.adminUnitsList[0].guid
                }));

                this.$selectedVersion.set(this.$model().version);
            }

            this.initializeCollections();
        } catch (error) {
            this.toastr.error(Enums.Messages.GeneralError, Enums.Messages.Error, Utils.getToastrErrorOptions());
            console.error(error);
            this.router.navigate([Enums.PagesPaths.FlowDefinitionList]);
        } finally {
            this.spinner.hide();
        }
    }

    ngOnDestroy() {
        this.$clearLinksTrigger.set(true);
    }

    onResize(event) {
        if (event.type != 'wheel') {
            this.$linkFlowObjectsTrigger.set(true);
        }
    }

    dragStarted(event: CdkDragStart, isAvailableFlowObject = false) {
        if (this.$isReadOnlyMode()) return;

        this.draggingFlowObjectIndex = this.$availableFlowObjects().findIndex(item => item.typeId.toString() == event.source.element.nativeElement.dataset.type);

        if (isAvailableFlowObject) {
            this.flowAreaBackdropRef.nativeElement.classList.remove('hidden');
        } else {
            this.$clearLinksTrigger.set(true);
        }
    }

    dragEnded(event: CdkDragEnd) {
        if (this.$isReadOnlyMode()) return;

        this.flowAreaBackdropRef.nativeElement.classList.add('hidden');
        this.dragBeenOutsideAvailableArea = false;
    }

    dragMoved(event: CdkDragMove) {
        if (this.$isReadOnlyMode()) return;

        let dropArea = this.flowAreaRef.nativeElement.getBoundingClientRect();
        let sourceArea = this.availableFlowObjectsParentRef.nativeElement.getBoundingClientRect();

        if (this.isMouseInsideArea(event.pointerPosition, dropArea, 2)) {
            this.availableFlowObjectsWrapperRef.nativeElement.insertBefore(
                document.querySelector('.cdk-drag-placeholder'),
                this.draggingFlowObjectIndex == this.$availableFlowObjects().length - 1
                    ? null
                    : this.availableFlowObjectsWrapperRef.nativeElement.children[this.draggingFlowObjectIndex]
            );

            this.flowAreaRef.nativeElement.classList.add('droppable');
        } else {
            this.flowAreaRef.nativeElement.classList.remove('droppable');

            if (this.isMouseOutsideArea(event.pointerPosition, sourceArea)) {
                this.dragBeenOutsideAvailableArea = true;
            } else if (this.isMouseInsideArea(event.pointerPosition, sourceArea) && this.dragBeenOutsideAvailableArea) {
                document.dispatchEvent(new MouseEvent('mouseup'));
            }
        }
    }

    dragDropped(event: CdkDragDrop<FlowObjectDefinition[]>) {
        if (this.$isReadOnlyMode()) return;

        if (event.previousContainer !== event.container) {
            this.selectFlowObject(event.previousContainer.data[event.previousIndex]);
            this.flowAreaRef.nativeElement.classList.remove('droppable');
        } else {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
            this.$reorderFlowObjectsTrigger.set(true);
            this.$linkFlowObjectsTrigger.set(true);
        }

        this.draggingFlowObjectIndex = null;
    }

    isOverflown(elem: HTMLElement): boolean {
        let flowObjectBodyContainer = this.flowObjectBodyContainerRef.toArray().find(x => Array.from(elem.children).includes(x.nativeElement))?.nativeElement;
        if (flowObjectBodyContainer == null) return false;

        // compensação por um fator de ajuste empírico
        return flowObjectBodyContainer.scrollWidth > elem.clientWidth * .87;
    }

    selectFlowObject(flowObject: FlowObjectDefinition, isFirstGatewayPath = false, isSecondGatewayPath = false) {
        if (this.$isReadOnlyMode()) return;

        const hadGatewayPathBefore = this.$hasGatewayPath();

        // #region [adição do Grouping e GroupingColumns principais]
        let newGrouping = new Grouping({
            outerIndex: this.getNextOuterIndexGroupings(isFirstGatewayPath, isSecondGatewayPath),
            typeId: GroupingType.Default,
            ownerId: this.$currentUser().id,
            isFirstGatewayPath: isFirstGatewayPath,
            isSecondGatewayPath: isSecondGatewayPath
        });

        let newGroupingColumn_0 = new GroupingColumn({
            outerIndex: this.getNextOuterIndexGroupingColumns(isFirstGatewayPath, isSecondGatewayPath),
            groupingId: newGrouping.id,
            grouping: newGrouping,
            isFirstGatewayPath: isFirstGatewayPath,
            isSecondGatewayPath: isSecondGatewayPath
        });
        // #endregion

        let configSchema = null;
        let formSchema = null;
        let documentHtmlContent = null;

        // #region [tratamento padrão das propriedades que abriguem código-fonte (JSON/HTML)]
        if (
            [
                FlowObjectType.StartForm,
                FlowObjectType.StartInboundApi,
                FlowObjectType.TaskForward,
                FlowObjectType.TaskAcknowledge,
                FlowObjectType.TaskRegisterProcess,
                FlowObjectType.TaskDispatchProcess,
                FlowObjectType.TaskAddToProcess,
                FlowObjectType.GatewayApprove,
                FlowObjectType.GatewayPaths,
                FlowObjectType.FinishOutboundApi,
                FlowObjectType.FinishContent
            ].includes(flowObject.typeId)
        ) {
            configSchema = JSON.stringify(new ConfigSchema());

            if (flowObject.typeId == FlowObjectType.StartForm) {
                formSchema = JSON.stringify(FORMBUILDER_SCHEMA_DEFAULT);
                documentHtmlContent = HTML_TEMPLATE_DEFAULT;
            } else if (flowObject.typeId == FlowObjectType.StartInboundApi) {
                formSchema = JSON.stringify(FORMBUILDER_SCHEMA_DEFAULT);
            } else if (flowObject.typeId == FlowObjectType.TaskAcknowledge) {
                documentHtmlContent = HTML_TEMPLATE_ACKNOWLEDGE;
            } else if (flowObject.typeId == FlowObjectType.GatewayApprove) {
                documentHtmlContent = HTML_TEMPLATE_APPROVE;
            } else if (flowObject.typeId == FlowObjectType.FinishContent) {
                documentHtmlContent = HTML_TEMPLATE_EXTRA_CONTENT;
            }
        }
        // #endregion

        // #region [adição do FlowObjectDefinition principal]
        let newFlowObject_0 = new FlowObjectDefinition({
            name: flowObject.typeId == FlowObjectType.StartForm ? FlowObjectTypeEDocsName.get(flowObject.typeId) : flowObject.name,
            publicName: flowObject.publicName,
            configSchema: configSchema,
            formSchema: formSchema,
            documentHtmlContent: documentHtmlContent,
            version: this.$selectedVersion(),
            outerIndex: this.getNextOuterIndexFlowObjects(isFirstGatewayPath, isSecondGatewayPath),
            typeId: flowObject.typeId,
            isFlowStarter: TaskRules.getStarterTaskTypes().includes(flowObject.typeId) ? true : false,
            isFirstGatewayPath: isFirstGatewayPath,
            isSecondGatewayPath: isSecondGatewayPath,
            flowDefinitionId: this.$model().id,
            groupingColumnId: newGroupingColumn_0.id,
            groupingColumn: newGroupingColumn_0
        });
        // #endregion

        newGrouping.groupingColumns.push(newGroupingColumn_0);
        newGroupingColumn_0.flowObjectDefinitions.push(newFlowObject_0);

        let newGroupingColumnList = [newGroupingColumn_0];
        let newFlowObjectList = [newFlowObject_0];

        // #region [configuração específica para Tarefas do tipo "Divisão de Fluxo"]
        if (flowObject.typeId == FlowObjectType.GatewayPaths) {
            newGrouping.typeId = GroupingType.Gateway;

            let newGroupingColumn_1 = new GroupingColumn({
                innerIndex: 1,
                outerIndex: this.getNextOuterIndexGroupingColumns(isFirstGatewayPath, isSecondGatewayPath) + 1,
                groupingId: newGrouping.id,
                grouping: newGrouping
            });

            newGrouping.groupingColumns.push(newGroupingColumn_1);
            newGroupingColumnList.push(newGroupingColumn_1);

            let newFlowObject_1 = new FlowObjectDefinition({
                name: 'Sequência A',
                typeId: FlowObjectType.Dummy,
                innerIndex: 0,
                outerIndex: this.getNextOuterIndexFlowObjects(isFirstGatewayPath, isSecondGatewayPath) + 1,
                version: this.$selectedVersion(),
                flowDefinitionId: this.$model().id,
                groupingColumnId: newGroupingColumn_1.id,
                groupingColumn: newGroupingColumn_1
            });

            newGroupingColumn_1.flowObjectDefinitions.push(newFlowObject_1);
            newFlowObjectList.push(newFlowObject_1);

            let newFlowObject_2 = new FlowObjectDefinition({
                name: 'Sequência B',
                typeId: FlowObjectType.Dummy,
                innerIndex: 1,
                outerIndex: this.getNextOuterIndexFlowObjects(isFirstGatewayPath, isSecondGatewayPath) + 2,
                version: this.$selectedVersion(),
                flowDefinitionId: this.$model().id,
                groupingColumnId: newGroupingColumn_1.id,
                groupingColumn: newGroupingColumn_1
            });

            newGroupingColumn_1.flowObjectDefinitions.push(newFlowObject_2);
            newFlowObjectList.push(newFlowObject_2);
        }
        // #endregion

        // #region [configuração específica para Tarefas de Gateway que não sejam do tipo "Divisão de Fluxo"]
        if ([FlowObjectType.GatewayApprove, FlowObjectType.GatewayRules].includes(flowObject.typeId)) {
            newGrouping.typeId = GroupingType.Gateway;

            let newGroupingColumn_1 = new GroupingColumn({
                innerIndex: 1,
                outerIndex: this.getNextOuterIndexGroupingColumns(isFirstGatewayPath, isSecondGatewayPath) + 1,
                groupingId: newGrouping.id,
                grouping: newGrouping,
                isFirstGatewayPath: isFirstGatewayPath,
                isSecondGatewayPath: isSecondGatewayPath
            });

            newGrouping.groupingColumns.push(newGroupingColumn_1);
            newGroupingColumnList.push(newGroupingColumn_1);

            let newFlowObject_1 = new FlowObjectDefinition({
                configSchema: JSON.stringify(new ConfigSchema()),
                outerIndex: this.getNextOuterIndexFlowObjects(isFirstGatewayPath, isSecondGatewayPath) + 1,
                version: this.$selectedVersion(),
                typeId: FlowObjectType.TaskForward,
                flowDefinitionId: this.$model().id,
                groupingColumnId: newGroupingColumn_1.id,
                groupingColumn: newGroupingColumn_1,
                isFirstGatewayPath: isFirstGatewayPath,
                isSecondGatewayPath: isSecondGatewayPath,
            });

            newGroupingColumn_1.flowObjectDefinitions.push(newFlowObject_1);
            newFlowObjectList.push(newFlowObject_1);

            let newFlowObject_2 = new FlowObjectDefinition({
                innerIndex: 1,
                outerIndex: this.getNextOuterIndexFlowObjects(isFirstGatewayPath, isSecondGatewayPath) + 2,
                version: this.$selectedVersion(),
                typeId: FlowObjectType.Finish,
                flowDefinitionId: this.$model().id,
                groupingColumnId: newGroupingColumn_1.id,
                groupingColumn: newGroupingColumn_1,
                isFirstGatewayPath: isFirstGatewayPath,
                isSecondGatewayPath: isSecondGatewayPath,
            });

            newGroupingColumn_1.flowObjectDefinitions.push(newFlowObject_2);
            newFlowObjectList.push(newFlowObject_2);
        }
        // #endregion

        Utils.updSignal(this.$selectedGroupings, x => {
            x.push(newGrouping);
            return x;
        });
        this.$selectedGroupingColumns.set(this.$selectedGroupingColumns().concat(newGroupingColumnList));
        this.$selectedFlowObjects.set(this.$selectedFlowObjects().concat(newFlowObjectList));

        this.$onAfterSelectFlowObjectTrigger.set({
            shouldMoveGroupingBackwards: hadGatewayPathBefore && !isFirstGatewayPath && !isSecondGatewayPath,
            flowObject: newFlowObject_0
        });
    }

    updateFlowObject(flowObject: FlowObjectDefinition) {
        if (this.$isReadOnlyMode()) return;

        // #region [atualiza o FlowObjectDefinition na collection de controle]
        if (TaskRules.getConfigSchemaDependentOnMainTabTaskTypes().includes(flowObject.typeId)) {
            Utils.updSignal(this.$selectedFlowObjects, x => {
                x[flowObject.outerIndex].configSchema = flowObject.configSchema;
                return x;
            });
        }

        switch (this.$activeDetailsTab()) {
            case Enums.DetailsAreaTabs.FlowObjectDetails:
                Utils.updSignal(this.$selectedFlowObjects, x => {
                    x[flowObject.outerIndex].name = flowObject.name;
                    x[flowObject.outerIndex].publicName = flowObject.publicName;
                    x[flowObject.outerIndex].publicMessageHtml = flowObject.publicMessageHtml;
                    x[flowObject.outerIndex].configSchema = flowObject.configSchema;
                    return x;
                });
                break;

            case Enums.DetailsAreaTabs.FormBuilder:
            case Enums.DetailsAreaTabs.FormBuilderInboundApi:
                Utils.updSignal(this.$selectedFlowObjects, x => {
                    x[flowObject.outerIndex].formSchema = flowObject.formSchema;
                    return x;
                });
                break;

            case Enums.DetailsAreaTabs.HtmlEditor:
                Utils.updSignal(this.$selectedFlowObjects, x => {
                    x[flowObject.outerIndex].documentHtmlContent = flowObject.documentHtmlContent;
                    return x;
                });
                break;

            case Enums.DetailsAreaTabs.GatewayPaths:
                Utils.updSignal(this.$selectedFlowObjects, x => {
                    const configSchema = JSON.parse(flowObject.configSchema) as ConfigSchema;
                    x[flowObject.outerIndex + 1].name = configSchema.taskGatewayPaths.firstGatewayPathName;
                    x[flowObject.outerIndex + 2].name = configSchema.taskGatewayPaths.secondGatewayPathName;
                    return x;
                });
                break;
        }

        Utils.updSignal(this.$selectedFlowObjects, x => {
            x[flowObject.outerIndex].isFlowStarter = flowObject.isFlowStarter;
            return x;
        });
        // #endregion

        this.toastr.success(Enums.Messages.SketchSaveSuccess, Enums.Messages.Success);
    }

    async updateFlowDefinition(flowDefinition: FlowDefinition, isFromHotConfigs?: boolean) {
        Utils.setSignal(this.$model, Object.assign(Object.assign(new FlowDefinition(), this.$model()), flowDefinition));

        if (isFromHotConfigs) {
            await this.saveFlowDefinition(true);
        }

        if (!isFromHotConfigs) {
            this.toastr.success(Enums.Messages.SketchSaveSuccess, Enums.Messages.Success);
        }
    }

    async saveFlowDefinition(isFromHotConfigs?: boolean) {
        if (
            (
                this.$isReadOnlyMode()
                && !isFromHotConfigs
            ) || (
                this.isCreate
                && this.$selectedFlowObjects().length == 0
            )
        ) return false;

        await this.proceedWithSaveFlowDefinition(isFromHotConfigs);
    }

    getAvailableFlowObjectFeatureHint(flowObject: FlowObjectDefinition): string {
        switch (flowObject.typeId) {
            case FlowObjectType.StartForm:
                return 'availableFlowObjectStartForm';
            case FlowObjectType.StartInboundApi:
                return 'availableFlowObjectStartInboundApi';
            case FlowObjectType.TaskForward:
                return 'availableFlowObjectTaskForward';
            case FlowObjectType.TaskAcknowledge:
                return 'availableFlowObjectTaskAcknowledge';
            case FlowObjectType.TaskMidwayOutboundApi:
                return 'availableFlowObjectTaskMidwayOutboundApi';
            case FlowObjectType.TaskRegisterProcess:
                return 'availableFlowObjectTaskRegisterProcess';
            case FlowObjectType.TaskDispatchProcess:
                return 'availableFlowObjectTaskDispatchProcess';
            case FlowObjectType.TaskAddToProcess:
                return 'availableFlowObjectTaskAddToProcess';
            case FlowObjectType.GatewayApprove:
                return 'availableFlowObjectGatewayApprove';
            case FlowObjectType.GatewayRules:
                return 'availableFlowObjectGatewayRules';
            case FlowObjectType.GatewayPaths:
                return 'availableFlowObjectGatewayPaths';
            case FlowObjectType.Finish:
                return 'availableFlowObjectFinish';
            case FlowObjectType.FinishContent:
                return 'availableFlowObjectFinishContent';
            case FlowObjectType.FinishOutboundApi:
                return 'availableFlowObjectFinishOutboundApi';
            default:
                return null;
        }
    }

    // #region [===== private methods =====]
    // #endregion

    private initializeCollections() {
        if (!this.isCreate) {
            this.$selectedFlowObjects.set(this.$model().flowObjectDefinitions.toSorted((a, b) => a.outerIndex - b.outerIndex));

            this.$selectedFlowObjects().forEach(item => {
                if (!this.$selectedGroupingColumns().some(x => x.id == item.groupingColumnId)) {
                    Utils.updSignal(this.$selectedGroupingColumns, x => {
                        x.push(item.groupingColumn);
                        return x;
                    });
                }
            });
            this.$selectedGroupingColumns.set(this.$selectedGroupingColumns().toSorted((a, b) => a.outerIndex - b.outerIndex));

            this.$selectedGroupingColumns().forEach(item => {
                if (!this.$selectedGroupings().some(x => x.id == item.groupingId)) {
                    Utils.updSignal(this.$selectedGroupings, x => {
                        x.push(item.grouping);
                        return x;
                    });
                }

                item.flowObjectDefinitions = item.flowObjectDefinitions.concat(this.$selectedFlowObjects().filter(x => x.groupingColumnId == item.id));
            });
            this.$selectedGroupings.set(this.$selectedGroupings().toSorted((a, b) => a.outerIndex - b.outerIndex));

            Utils.updSignal(this.$selectedGroupings, x => {
                x.forEach(item => {
                    item.groupingColumns = item.groupingColumns.concat(this.$selectedGroupingColumns().filter(x => x.groupingId == item.id));
                });
                return x;
            });
        }

        // recupera as Tarefas habilitadas para utilização (especificadas no appsettings.json, relay via cookie)
        let enabledTasks = this.getEnabledTasks();
        if (enabledTasks != null) {
            // caso haja especificação de Tarefas, apenas uma das duas listas é considerada
            if (enabledTasks.whitelist != null) {
                enabledTasks.whitelist.forEach((item: string) => {
                    if (
                        FlowObjectType[item.trim()] != null
                        && ![
                            FlowObjectType.Dummy,
                            FlowObjectType.CancelByDeliberation,
                            FlowObjectType.CancelBySchedule,
                        ].includes(+item)
                    ) {
                        Utils.updSignal(this.$availableFlowObjects, x => {
                            x.push(new FlowObjectDefinition({ typeId: FlowObjectType[item.trim()] }));
                            return x;
                        });
                    }
                });
            } else if (enabledTasks.blacklist != null) {
                for (let item in FlowObjectType) {
                    if (
                        Number.isInteger(+item)
                        && !enabledTasks.blacklist.includes(FlowObjectType[item.trim()])
                        && ![
                            FlowObjectType.Dummy,
                            FlowObjectType.CancelByDeliberation,
                            FlowObjectType.CancelBySchedule,
                        ].includes(+item)
                    ) {
                        Utils.updSignal(this.$availableFlowObjects, x => {
                            x.push(new FlowObjectDefinition({ typeId: +item }));
                            return x;
                        });
                    }
                }
            }
        } else {
            // caso não haja especificação de Tarefas, exibe todas as disponíveis
            for (let item in FlowObjectType) {
                if (
                    Number.isInteger(+item)
                    && ![
                        FlowObjectType.Dummy,
                        FlowObjectType.CancelByDeliberation,
                        FlowObjectType.CancelBySchedule,
                    ].includes(+item)
                ) {
                    Utils.updSignal(this.$availableFlowObjects, x => {
                        x.push(new FlowObjectDefinition({ typeId: +item }));
                        return x;
                    });
                }
            }
        }

        setTimeout(() => {
            this.spinner.hide();
            this.$isBootstrapFinished.set(true);
        }, 800);
    }

    private isMouseInsideArea(pointerPosition, area, offset?): boolean {
        offset = offset || 0;

        return pointerPosition.x > area.left - offset
            && pointerPosition.x < area.right + offset
            && pointerPosition.y > area.top - offset
            && pointerPosition.y < area.bottom + offset;
    }

    private isMouseOutsideArea(pointerPosition, area, offset?): boolean {
        offset = offset || 0;

        return pointerPosition.x < area.left - offset
            || pointerPosition.x > area.right + offset
            || pointerPosition.y < area.top - offset
            || pointerPosition.y > area.bottom + offset;
    }

    private getNextOuterIndexGroupings(isFirstGatewayPath: boolean, isSecondGatewayPath: boolean): number {
        return isFirstGatewayPath
            ? this.$mainPathGroupings().length + this.$firstGatewayPathGroupings().length
            : isSecondGatewayPath
                ? this.$mainPathGroupings().length + this.$firstGatewayPathGroupings().length + this.$secondGatewayPathGroupings().length
                : this.$mainPathGroupings().length;
    }

    private getNextOuterIndexGroupingColumns(isFirstGatewayPath: boolean, isSecondGatewayPath: boolean): number {
        return isFirstGatewayPath
            ? this.$mainPathGroupingColumns().length + this.$firstGatewayPathGroupingColumns().length
            : isSecondGatewayPath
                ? this.$mainPathGroupingColumns().length + this.$firstGatewayPathGroupingColumns().length + this.$secondGatewayPathGroupingColumns().length
                : this.$mainPathGroupingColumns().length;
    }

    private getNextOuterIndexFlowObjects(isFirstGatewayPath: boolean, isSecondGatewayPath: boolean): number {
        return isFirstGatewayPath
            ? this.$mainPathFlowObjects().length + this.$firstGatewayPathFlowObjects().length
            : isSecondGatewayPath
                ? this.$mainPathFlowObjects().length + this.$firstGatewayPathFlowObjects().length + this.$secondGatewayPathFlowObjects().length
                : this.$mainPathFlowObjects().length;
    }

    private getEnabledTasks(): any {
        // lê os valores dos cookies de Tarefas habilitadas
        let whitelist = atob(this.cookieService.get('prodest-eflow-atw')).split(/[ ;,\.\-]/gi).filter(x => x != '');
        let blacklist = atob(this.cookieService.get('prodest-eflow-atb')).split(/[ ;,\.\-]/gi).filter(x => x != '');

        // caso ambos os cookies não existam ou ambos possuam valor, ignorá-los
        if (
            (whitelist.length == 0 && blacklist.length == 0)
            || (whitelist.length > 0 && blacklist.length > 0)
        ) {
            return null;
        }

        if (whitelist.length == 0) {
            return { blacklist: blacklist };
        }

        return { whitelist: whitelist };
    }

    private async proceedWithSaveFlowDefinition(isFromHotConfigs?: boolean) {
        if (!this.$isReadOnlyMode()) {
            Utils.updSignal(this.$model, x => {
                x.flowObjectDefinitions = this.$selectedFlowObjects();
                return x;
            });
        }

        this.spinner.show('flow');

        let response;
        if (this.isCreate) {
            response = await this.flowDefinitionService.insert(this.$model());
        } else if (isFromHotConfigs == true) {
            response = await this.flowDefinitionService.updateRoot(this.$model());
        } else {
            response = await this.flowDefinitionService.update(this.$model());
        }

        if (!response.isSuccess) {
            this.toastr.error(
                response.message.description,
                Enums.Messages.Error,
                Utils.getToastrErrorOptions()
            );
            this.spinner.hide('flow');
            return false;
        }

        this.toastr.success(response.message.description, Enums.Messages.Success);

        if (this.isCreate) {
            this.router.navigate([Enums.PagesPaths.FlowDefinition, this.$model().id]);
        }

        this.spinner.hide('flow');
    }
}
